import { useEffect } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useForm, type Path } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import {
  companyApi,
  companyQuery,
  type CompanyProfile,
  type CompanyProfilePatch,
} from "@/lib/company";
import { codeOptions, countryOptions } from "@/lib/options";
import { reference } from "@/lib/reference";

export const Route = createFileRoute("/_authenticated/settings/company")({
  beforeLoad: ({ context }) => {
    if (!context.user.is_admin) throw redirect({ to: "/" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(companyQuery),
  component: CompanyPage,
});

function toValues(profile: CompanyProfile): CompanyProfilePatch {
  const { id: _id, updated_at: _updated, ...rest } = profile;
  return rest;
}

function CompanyPage() {
  const { data: profile } = useSuspenseQuery(companyQuery);
  const queryClient = useQueryClient();
  const form = useForm<CompanyProfilePatch>({ defaultValues: toValues(profile) });
  const { register, watch, setValue, reset, formState } = form;

  useEffect(() => {
    if (!formState.isDirty) reset(toValues(profile));
  }, [profile, reset, formState.isDirty]);

  const lists = {
    declarationTypes: useQuery(reference.declarationTypes),
    customsOffices: useQuery(reference.customsOffices),
    locations: useQuery(reference.locations),
    ports: useQuery(reference.ports),
    countries: useQuery(reference.countries),
    transportModes: useQuery(reference.transportModes),
    natures: useQuery(reference.naturesOfTransaction),
    incoTerms: useQuery(reference.incoTerms),
    currencies: useQuery(reference.currencies),
  };

  const save = useMutation({
    mutationFn: companyApi.update,
    onSuccess: (updated) => {
      queryClient.setQueryData(companyQuery.queryKey, updated);
      reset(toValues(updated));
      toast.success("Company profile saved");
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not save"),
  });

  const pick = (name: Path<CompanyProfilePatch>) => (value: string | null) =>
    setValue(name, value as never, { shouldDirty: true });

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Company</h1>
        <p className="text-muted-foreground text-sm">
          The declarant on every export, and the values new bills of lading start with.
        </p>
      </div>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Declarant</CardTitle>
            <CardDescription>
              Goes into the Declarant and Financial segments of the XML.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...register("name")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="crib">CRIB / declarant code</Label>
              <Input id="crib" {...register("crib_number")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deferred">Deferred payment reference</Label>
              <Input
                id="deferred"
                {...register("deferred_payment_reference")}
                placeholder="MASTERCARGO 29"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="address">Address</Label>
              <Input id="address" {...register("address")} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Defaults for new bills of lading</CardTitle>
            <CardDescription>Every one of these can still be changed per bill.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Pick
              label="Declaration type"
              value={watch("default_declaration_type")}
              onChange={pick("default_declaration_type")}
              options={codeOptions(lists.declarationTypes.data)}
            />
            <Pick
              label="Currency"
              value={watch("default_currency")}
              onChange={pick("default_currency")}
              options={(lists.currencies.data ?? []).map((c) => ({
                value: c.id,
                label: c.code,
                description: c.name,
              }))}
            />
            <Pick
              label="Office of entry"
              value={watch("default_office_of_entry")}
              onChange={pick("default_office_of_entry")}
              options={codeOptions(lists.customsOffices.data)}
            />
            <Pick
              label="Office of destination"
              value={watch("default_office_of_destination")}
              onChange={pick("default_office_of_destination")}
              options={codeOptions(lists.customsOffices.data)}
            />
            <Pick
              label="Location of goods"
              value={watch("default_location_of_goods")}
              onChange={pick("default_location_of_goods")}
              options={codeOptions(lists.locations.data)}
            />
            <Pick
              label="Place of unloading"
              value={watch("default_port_of_unloading")}
              onChange={pick("default_port_of_unloading")}
              options={codeOptions(lists.ports.data)}
            />
            <Pick
              label="Country of destination"
              value={watch("default_country_of_destination")}
              onChange={pick("default_country_of_destination")}
              options={countryOptions(lists.countries.data)}
            />
            <Pick
              label="Mode of transport"
              value={watch("default_transport_mode")}
              onChange={pick("default_transport_mode")}
              options={codeOptions(lists.transportModes.data)}
            />
            <Pick
              label="Nature of transaction"
              value={watch("default_nature_of_transaction")}
              onChange={pick("default_nature_of_transaction")}
              options={codeOptions(lists.natures.data)}
            />
            <Pick
              label="Delivery terms"
              value={watch("default_inco_term")}
              onChange={pick("default_inco_term")}
              options={codeOptions(lists.incoTerms.data)}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!formState.isDirty}
            onClick={() => reset(toValues(profile))}
          >
            Discard
          </Button>
          <Button type="submit" disabled={!formState.isDirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Pick({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  options: { value: string; label: string; description?: string }[];
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Combobox value={value ?? null} onChange={onChange} options={options} />
    </div>
  );
}
